/**
 * ClinicalRecords.tsx — Módulo de Fichas Clínicas
 *
 * Vista 1: Lista de pacientes (tabla tipo macOS)
 * Vista 2: Ficha clínica detallada por paciente
 *
 * Contexto B — Portal Terapeuta (TherapistPortal.css tokens)
 * Mock data hardcodeada — lógica Supabase se agrega en fase siguiente.
 *
 * Restricciones:
 * - Sin console.log con datos de pacientes
 * - Sin servicios externos para avatares
 * - Sin tokens del Contexto A (modern-style.css)
 */

import React, { useState, useMemo, useEffect } from 'react';
import './ClinicalRecords.css';
import { supabase } from '../../lib/supabase/client';
import { getRoadmapBloqueo, setRoadmapBloqueo, setRoadmapProgreso } from '../../services/roadmap.service';
import AppointmentScheduler from '../TherapistCalendar/AppointmentScheduler';
import PatientRegistration from './PatientRegistration';
import SessionNoteEditor from './SessionNoteEditor';
import CertificateGenerator from './CertificateGenerator';
import { TableSkeleton, RecordSkeleton } from '../ui/Skeleton';
import type { TherapistIdentity } from '../../lib/clinicalExport/types';
import type { SessionRecord } from '../../lib/clinicalRecords/types';

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface ClinicalRecordsModuleProps {
  therapist: TherapistIdentity;
  adminMode?: boolean;
  compactRegistration?: boolean;
}

interface PatientWithStats {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  rut: string;
  fecha_nacimiento?: string;
  prevision?: string;
  activo: boolean;
  estado_relacion: EstadoRelacion;
  // Sanción administrativa (sólo director). ORTOGONAL a `activo` y a
  // `estado_relacion`. Ver docs/plan-estado-y-canal-paciente.md + migración
  // bloqueo_clinica_paciente_2026_09_02.sql.
  bloqueado_clinica?: boolean;
  total_sesiones: number;
  ultima_sesion?: string;
  proxima_sesion?: string;
  terapeuta_id?: string;
  terapeuta_nombre?: string;
  terapeuta_apellido?: string;
  terapeuta_rut?: string;
  terapeuta_titulo?: string;
  terapeuta_registro_profesional?: string;
  terapeuta_numero_licencia?: string;
}

interface ClinicalRecord {
  paciente: PatientWithStats & {
    direccion?: string;
    comuna?: string;
    ciudad?: string;
    nombre_isapre?: string;
    numero_beneficiario?: string;
    contacto_emergencia_nombre?: string;
    contacto_emergencia_telefono?: string;
    contacto_emergencia_relacion?: string;
    alergias?: string;
    medicamentos_actuales?: string;
    condiciones_medicas?: string;
    tarifa_diferenciada?: number | null;
    tarifa_diferenciada_nota?: string | null;
    nombre_pareja?: string | null;
    bloqueo_motivo?: string | null;
    bloqueo_at?: string | null;
  };
  sesiones: SessionRecord[];
  // Roadmap Feature 2 (docs/plan-ruta-terapeutica-pareja.md §7.4/§7.8) — clave
  // agregada por get_patient_clinical_record[_admin]. `null`/ausente = el
  // paciente no tiene ninguna consulta 'pareja' ni override: cr-roadmap-control
  // no se muestra.
  roadmap?: { pareja: RoadmapParejaInfo | null } | null;
}

interface RoadmapParejaInfo {
  sesion_actual: number;
  modo: 'auto' | 'manual';
  total: number;
}


// ─── Helpers ──────────────────────────────────────────────────────────────────

// Evita el corrimiento de un día: "1990-05-15" es DATE (sin hora), pero
// `new Date(iso)` lo parsea como UTC medianoche y toLocaleDateString lo
// formatea en zona horaria local (Chile, UTC-3/-4) — cae al día anterior.
function parseLocalDate(iso: string): Date {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return parseLocalDate(iso).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatShortDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function calcAge(fechaNacimiento?: string): string {
  if (!fechaNacimiento) return '—';
  const birth = parseLocalDate(fechaNacimiento);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return `${age} años`;
}

function getInitials(nombre: string, apellido: string): string {
  return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
}

function labelPrevision(prev?: string): string {
  if (!prev) return '—';
  const map: Record<string, string> = {
    isapre: 'Isapre',
    fonasa: 'Fonasa',
    particular: 'Particular',
  };
  return map[prev] ?? prev;
}

// ─── Estado de relación del paciente ─────────────────────────────────────────
// ORTOGONAL a `activo` (soft-delete). Persiste en pacientes.estado_relacion,
// se escribe con la RPC set_patient_relationship_status. Ver
// docs/plan-estado-y-canal-paciente.md §4.

type EstadoRelacion = 'activo' | 'en_pausa' | 'alta' | 'desercion' | 'derivado';

const ESTADOS_RELACION: EstadoRelacion[] = ['activo', 'en_pausa', 'alta', 'desercion', 'derivado'];

// Clases cr-badge--* reutilizadas: contraste AA ya verificado (ver ClinicalRecords.css).
const RELACION_META: Record<EstadoRelacion, { label: string; badge: string }> = {
  activo:    { label: 'En terapia', badge: 'cr-badge--green' },
  en_pausa:  { label: 'En pausa',   badge: 'cr-badge--orange' },
  alta:      { label: 'Alta',       badge: 'cr-badge--blue' },
  desercion: { label: 'Deserción',  badge: 'cr-badge--muted' },
  derivado:  { label: 'Derivado',   badge: 'cr-badge--neutral' },
};

function normalizeEstadoRelacion(v: unknown): EstadoRelacion {
  return (ESTADOS_RELACION as string[]).includes(v as string) ? (v as EstadoRelacion) : 'activo';
}

// En adminMode la lista del director incluye pacientes borrados (activo=false):
// para esos se muestra "Eliminado", no el estado de relación.
function RelationshipStatusBadge({ estado, activo }: { estado: EstadoRelacion; activo: boolean }) {
  if (!activo) return <span className="cr-badge cr-badge--muted">Eliminado</span>;
  const meta = RELACION_META[estado] ?? RELACION_META.activo;
  return <span className={`cr-badge ${meta.badge}`}>{meta.label}</span>;
}

// Sanción administrativa. Se muestra junto al badge de relación, nunca en su
// lugar (un paciente bloqueado conserva su estado clínico).
function BlockedBadge() {
  return (
    <span className="cr-badge cr-badge--red" title="Bloqueado por la clínica — no puede agendar">
      <i className="fas fa-ban" aria-hidden="true" style={{ marginRight: 4 }}></i>
      Bloqueado
    </span>
  );
}

// ─── Componente badge estado de sesión ───────────────────────────────────────

function SessionStatusBadge({ estado }: { estado: string }) {
  const modifiers: Record<string, string> = {
    completada: 'cr-badge--green',
    cancelada: 'cr-badge--red',
    programada: 'cr-badge--blue',
    agendada: 'cr-badge--blue',
    confirmada: 'cr-badge--blue',
    no_asistio: 'cr-badge--orange',
  };
  const labels: Record<string, string> = {
    completada: 'Completada',
    cancelada: 'Cancelada',
    programada: 'Programada',
    agendada: 'Agendada',
    confirmada: 'Confirmada',
    no_asistio: 'No asistió',
  };
  const mod = modifiers[estado] ?? 'cr-badge--blue';
  const label = labels[estado] ?? estado;
  return <span className={`cr-badge ${mod}`}>{label}</span>;
}

// ─── Historia clínica — línea de tiempo condensada (§2.2, construida 100% en frontend) ──

interface TimelineSummary {
  primeraSesion?: string;
  totalCompletadas: number;
  totalCanceladas: number;
  totalNoAsistio: number;
  motivosFrecuentes: { motivo: string; count: number }[];
  planEvolucion: { fecha: string; plan: string }[];
  tasaAdherencia?: number; // % — completadas / (completadas+canceladas+no_asistio)
  duracionTratamientoMeses?: number;
  cadenciaPromedioDias?: number;
}

function buildTimelineSummary(sesiones: SessionRecord[], activo: boolean): TimelineSummary {
  // sesiones vienen ordenadas DESC por fecha_hora (RPC) — se ordena ASC para hallar la primera
  const ordenadasAsc = [...sesiones].sort(
    (a, b) => new Date(a.fecha_hora).getTime() - new Date(b.fecha_hora).getTime()
  );

  let totalCompletadas = 0;
  let totalCanceladas = 0;
  let totalNoAsistio = 0;
  const motivoCount = new Map<string, number>();

  for (const s of sesiones) {
    if (s.estado === 'completada') totalCompletadas++;
    else if (s.estado === 'cancelada') totalCanceladas++;
    else if (s.estado === 'no_asistio') totalNoAsistio++;

    const motivo = s.motivo_consulta?.trim();
    if (motivo) motivoCount.set(motivo, (motivoCount.get(motivo) ?? 0) + 1);
  }

  const motivosFrecuentes = Array.from(motivoCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([motivo, count]) => ({ motivo, count }));

  const planEvolucion = ordenadasAsc
    .filter((s): s is SessionRecord & { plan_tratamiento: string } => Boolean(s.plan_tratamiento?.trim()))
    .slice(-5)
    .reverse()
    .map(s => ({ fecha: s.fecha_hora, plan: s.plan_tratamiento }));

  const denomAdherencia = totalCompletadas + totalCanceladas + totalNoAsistio;
  const tasaAdherencia = denomAdherencia > 0 ? Math.round((totalCompletadas / denomAdherencia) * 100) : undefined;

  const primeraFecha = ordenadasAsc[0]?.fecha_hora;
  const ultimaFecha = ordenadasAsc[ordenadasAsc.length - 1]?.fecha_hora;
  let duracionTratamientoMeses: number | undefined;
  if (primeraFecha) {
    const fin = activo ? new Date() : new Date(ultimaFecha ?? primeraFecha);
    const inicio = new Date(primeraFecha);
    duracionTratamientoMeses = Math.max(
      0,
      (fin.getFullYear() - inicio.getFullYear()) * 12 + (fin.getMonth() - inicio.getMonth())
    );
  }

  let cadenciaPromedioDias: number | undefined;
  if (ordenadasAsc.length >= 2) {
    const deltasDias: number[] = [];
    for (let i = 1; i < ordenadasAsc.length; i++) {
      const diff =
        (new Date(ordenadasAsc[i].fecha_hora).getTime() - new Date(ordenadasAsc[i - 1].fecha_hora).getTime()) /
        (1000 * 60 * 60 * 24);
      deltasDias.push(diff);
    }
    cadenciaPromedioDias = Math.round(deltasDias.reduce((a, b) => a + b, 0) / deltasDias.length);
  }

  return {
    primeraSesion: ordenadasAsc[0]?.fecha_hora,
    totalCompletadas,
    totalCanceladas,
    totalNoAsistio,
    motivosFrecuentes,
    planEvolucion,
    tasaAdherencia,
    duracionTratamientoMeses,
    cadenciaPromedioDias,
  };
}

function ClinicalTimeline({ sesiones, activo }: { sesiones: SessionRecord[]; activo: boolean }) {
  if (sesiones.length === 0) return null;
  const s = buildTimelineSummary(sesiones, activo);
  const adherenciaModifier =
    s.tasaAdherencia === undefined ? '' : s.tasaAdherencia >= 80 ? 'cr-quick-stat--good' : s.tasaAdherencia >= 50 ? 'cr-quick-stat--warn' : 'cr-quick-stat--bad';

  return (
    <section className="cr-info-card cr-timeline-section" aria-labelledby="cr-timeline-title">
      <h2 className="cr-info-card__title" id="cr-timeline-title">
        <i className="fas fa-history" aria-hidden="true"></i>
        Historia Clínica — Línea de Tiempo
      </h2>

      <div className="cr-quick-stats">
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">{formatShortDate(s.primeraSesion)}</span>
          <span className="cr-quick-stat__label">Primera sesión</span>
        </div>
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">{s.totalCompletadas}</span>
          <span className="cr-quick-stat__label">Completadas</span>
        </div>
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">{s.totalCanceladas}</span>
          <span className="cr-quick-stat__label">Canceladas</span>
        </div>
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">{s.totalNoAsistio}</span>
          <span className="cr-quick-stat__label">No asistió</span>
        </div>
        {s.tasaAdherencia !== undefined && (
          <div className={`cr-quick-stat ${adherenciaModifier}`}>
            <span className="cr-quick-stat__value">{s.tasaAdherencia}%</span>
            <span className="cr-quick-stat__label">Adherencia</span>
          </div>
        )}
        {s.duracionTratamientoMeses !== undefined && (
          <div className="cr-quick-stat">
            <span className="cr-quick-stat__value">{s.duracionTratamientoMeses}</span>
            <span className="cr-quick-stat__label">Meses en tratamiento</span>
          </div>
        )}
        {s.cadenciaPromedioDias !== undefined && (
          <div className="cr-quick-stat">
            <span className="cr-quick-stat__value">{s.cadenciaPromedioDias}</span>
            <span className="cr-quick-stat__label">Días entre sesiones (prom.)</span>
          </div>
        )}
      </div>

      <dl className="cr-info-list">
        {s.motivosFrecuentes.length > 0 && (
          <div className="cr-info-row">
            <dt>Motivos de consulta frecuentes</dt>
            <dd>{s.motivosFrecuentes.map(m => `${m.motivo} (${m.count})`).join(' · ')}</dd>
          </div>
        )}
        {s.planEvolucion.map((p, i) => (
          <div className="cr-info-row" key={i}>
            <dt>{formatShortDate(p.fecha)} — Plan de tratamiento</dt>
            <dd>{p.plan.length > 160 ? `${p.plan.slice(0, 160)}…` : p.plan}</dd>
          </div>
        ))}
        {s.motivosFrecuentes.length === 0 && s.planEvolucion.length === 0 && (
          <div className="cr-info-row">
            <dd className="cr-text-muted">Sin suficientes datos clínicos registrados todavía.</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ─── Vista 1: Lista de pacientes ─────────────────────────────────────────────

interface PatientListViewProps {
  patients: PatientWithStats[];
  onSelectPatient: (patient: PatientWithStats) => void;
  onSchedule?: (patient: PatientWithStats) => void;
  onDelete?: (patient: PatientWithStats) => void;
  onRegister?: () => void;
  adminMode?: boolean;
}

function PatientListView({ patients, onSelectPatient, onSchedule, onDelete, onRegister, adminMode = false }: PatientListViewProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return patients;
    return patients.filter(
      (p) =>
        `${p.nombre ?? ''} ${p.apellido ?? ''}`.toLowerCase().includes(q) ||
        (p.rut ?? '').toLowerCase().includes(q) ||
        (p.email ?? '').toLowerCase().includes(q)
    );
  }, [patients, search]);

  const totalEnTerapia = patients.filter((p) => p.activo && p.estado_relacion === 'activo').length;
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const conSesionEsteMes = patients.filter((p) => {
    if (!p.ultima_sesion) return false;
    return new Date(p.ultima_sesion) >= startOfMonth;
  }).length;

  return (
    <div className="cr-list-view">
      {/* Header */}
      <div className="cr-list-header">
        <div className="cr-list-header__title-group">
          <h1 className="cr-title">{adminMode ? 'Todas las Fichas' : 'Mis Pacientes'}</h1>
          <p className="cr-subtitle">{adminMode ? 'Vista director — todos los pacientes de la clínica' : 'Fichas clínicas e historial de sesiones'}</p>
        </div>
        {!adminMode && onRegister && (
          <button className="cr-btn cr-btn--primary" onClick={onRegister}>
            <i className="fas fa-user-plus" aria-hidden="true"></i>
            Registrar Paciente
          </button>
        )}
      </div>

      {/* Stats chips */}
      <div className="cr-stats-row">
        <div className="cr-stat-chip">
          <span className="cr-stat-chip__value">{patients.length}</span>
          <span className="cr-stat-chip__label">Total</span>
        </div>
        <div className="cr-stat-chip cr-stat-chip--green">
          <span className="cr-stat-chip__value">{totalEnTerapia}</span>
          <span className="cr-stat-chip__label">En terapia</span>
        </div>
        <div className="cr-stat-chip cr-stat-chip--blue">
          <span className="cr-stat-chip__value">{conSesionEsteMes}</span>
          <span className="cr-stat-chip__label">Con sesión este mes</span>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="cr-search-wrap">
        <i className="fas fa-search cr-search-icon" aria-hidden="true"></i>
        <input
          className="cr-search-input"
          type="search"
          placeholder="Buscar por nombre, RUT o email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Buscar paciente"
        />
      </div>

      {/* Tabla */}
      {filtered.length === 0 ? (
        <div className="cr-empty-state">
          <i className="fas fa-users cr-empty-state__icon" aria-hidden="true"></i>
          <p className="cr-empty-state__title">No hay pacientes</p>
          <p className="cr-empty-state__body">
            {search
              ? 'No se encontraron resultados para tu búsqueda.'
              : 'Registrá tu primer paciente para comenzar.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop: tabla */}
          <div className="cr-table-wrap">
            <table className="cr-table" aria-label="Lista de pacientes">
              <thead>
                <tr>
                  <th scope="col">Paciente</th>
                  <th scope="col">RUT</th>
                  <th scope="col">Contacto</th>
                  {adminMode && <th scope="col">Terapeuta</th>}
                  <th scope="col" className="cr-col-num">Sesiones</th>
                  <th scope="col">Última sesión</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="cr-col-actions" aria-label="Acciones"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((patient) => (
                  <tr
                    key={patient.id}
                    className="cr-table__row"
                    onClick={() => onSelectPatient(patient)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Ver ficha de ${patient.nombre} ${patient.apellido}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectPatient(patient);
                      }
                    }}
                  >
                    <td className="cr-table__cell cr-cell-patient">
                      <span
                        className="patient-avatar patient-avatar--initials"
                        aria-hidden="true"
                      >
                        {getInitials(patient.nombre, patient.apellido)}
                      </span>
                      <span className="cr-patient-name">
                        {patient.nombre} {patient.apellido}
                      </span>
                    </td>
                    <td className="cr-table__cell cr-cell-mono">{patient.rut}</td>
                    <td className="cr-table__cell cr-cell-contact">
                      <span>{patient.email}</span>
                      {patient.telefono && (
                        <span className="cr-cell-contact__phone">
                          {patient.telefono}
                        </span>
                      )}
                    </td>
                    {adminMode && (
                      <td className="cr-table__cell cr-cell-mono">
                        {patient.terapeuta_nombre
                          ? `${patient.terapeuta_nombre} ${patient.terapeuta_apellido}`
                          : '—'}
                      </td>
                    )}
                    <td className="cr-table__cell cr-col-num">
                      <span className="cr-sessions-count">{patient.total_sesiones}</span>
                    </td>
                    <td className="cr-table__cell">
                      {formatShortDate(patient.ultima_sesion)}
                    </td>
                    <td className="cr-table__cell">
                      <div className="cr-badge-stack">
                        <RelationshipStatusBadge estado={patient.estado_relacion} activo={patient.activo} />
                        {patient.bloqueado_clinica && <BlockedBadge />}
                      </div>
                    </td>
                    <td className="cr-table__cell cr-col-actions" onClick={e => e.stopPropagation()}>
                      <div className="cr-action-group">
                        {onSchedule && (
                          <button
                            className="cr-action-btn cr-action-btn--schedule"
                            onClick={() => onSchedule(patient)}
                            title="Agendar cita"
                            aria-label={`Agendar cita para ${patient.nombre} ${patient.apellido}`}
                          >
                            <i className="fas fa-calendar-plus" aria-hidden="true"></i>
                          </button>
                        )}
                        {onDelete && (
                          <button
                            className="cr-action-btn cr-action-btn--delete"
                            onClick={() => onDelete(patient)}
                            title="Eliminar paciente"
                            aria-label={`Eliminar a ${patient.nombre} ${patient.apellido}`}
                          >
                            <i className="fas fa-trash" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: lista compacta */}
          <ul className="cr-mobile-list" aria-label="Lista de pacientes">
            {filtered.map((patient) => (
              <li key={patient.id} className="cr-mobile-item">
                <button
                  className="cr-mobile-item__btn"
                  onClick={() => onSelectPatient(patient)}
                  aria-label={`Ver ficha de ${patient.nombre} ${patient.apellido}`}
                >
                  <span
                    className="patient-avatar patient-avatar--initials"
                    aria-hidden="true"
                  >
                    {getInitials(patient.nombre, patient.apellido)}
                  </span>
                  <span className="cr-mobile-item__info">
                    <span className="cr-mobile-item__name">
                      {patient.nombre} {patient.apellido}
                    </span>
                    <span className="cr-mobile-item__rut">{patient.rut}</span>
                    <span className="cr-mobile-item__meta">
                      {patient.total_sesiones} sesiones · Última:{' '}
                      {formatShortDate(patient.ultima_sesion)}
                    </span>
                    {adminMode && (
                      <span className="cr-mobile-item__meta">
                        <i className="fas fa-user-md" style={{ marginRight: 4 }}></i>
                        {patient.terapeuta_nombre
                          ? `${patient.terapeuta_nombre} ${patient.terapeuta_apellido}`
                          : 'Sin asignar'}
                      </span>
                    )}
                  </span>
                  <span className="cr-mobile-item__right">
                    <span className="cr-badge-stack">
                      <RelationshipStatusBadge estado={patient.estado_relacion} activo={patient.activo} />
                      {patient.bloqueado_clinica && <BlockedBadge />}
                    </span>
                    <i className="fas fa-chevron-right cr-mobile-item__chevron" aria-hidden="true"></i>
                  </span>
                </button>
                {(onSchedule || onDelete) && (
                  <div className="cr-mobile-actions">
                    {onSchedule && (
                      <button
                        className="cr-mobile-action-btn cr-mobile-action-btn--schedule"
                        onClick={() => onSchedule(patient)}
                        aria-label={`Agendar cita para ${patient.nombre}`}
                      >
                        <i className="fas fa-calendar-plus" aria-hidden="true"></i>
                        <span>Agendar</span>
                      </button>
                    )}
                    {onDelete && (
                      <button
                        className="cr-mobile-action-btn cr-mobile-action-btn--delete"
                        onClick={() => onDelete(patient)}
                        aria-label={`Eliminar a ${patient.nombre}`}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                        <span>Eliminar</span>
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ─── Vista 2: Ficha Clínica ───────────────────────────────────────────────────

interface ClinicalRecordViewProps {
  patient: PatientWithStats;
  therapistId: string;
  // Identidad de quien está logueado. En adminMode es el DIRECTOR, no el
  // profesional tratante — el export PDF nunca debe firmar con esta identidad
  // en adminMode, solo con la del terapeuta real de cada sesión/paciente.
  callingTherapist: TherapistIdentity;
  onBack: () => void;
  onSchedule?: (patient: PatientWithStats) => void;
  onPatientUpdated?: (patientId: string, patch: Partial<PatientWithStats>) => void;
  adminMode?: boolean;
}

function ClinicalRecordView({ patient, therapistId, callingTherapist, onBack, onSchedule, onPatientUpdated, adminMode = false }: ClinicalRecordViewProps) {
  const [record, setRecord] = useState<ClinicalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Se incrementa para forzar recarga de la ficha (ej. tras bloquear, que
  // cancela consultas futuras server-side).
  const [reloadNonce, setReloadNonce] = useState(0);
  const [statusValue, setStatusValue] = useState<EstadoRelacion>(normalizeEstadoRelacion(patient.estado_relacion));
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSaved, setStatusSaved] = useState(false);
  // Bloqueo administrativo — sólo director (adminMode). RPC set_patient_clinic_block.
  const [blockPanelOpen, setBlockPanelOpen] = useState(false);
  const [blockMotivo, setBlockMotivo] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockError, setBlockError] = useState<string | null>(null);
  // Control manual del roadmap de pareja — plan §7.8. Solo visible si
  // record.roadmap.pareja no es null (el paciente tiene ≥1 consulta 'pareja' o
  // ya tiene un override). RPC set_roadmap_progreso — mismo patrón que
  // handleStatusSave: doble rama director/terapeuta dueño en el servidor.
  const [roadmapSesion, setRoadmapSesion] = useState<string>('1');
  const [roadmapSaving, setRoadmapSaving] = useState(false);
  const [roadmapError, setRoadmapError] = useState<string | null>(null);
  const [roadmapSaved, setRoadmapSaved] = useState(false);
  // FUNC-129 — ocultar el mapa de la ruta al paciente. `null` = sin cargar o
  // error de lectura (switch deshabilitado, nunca un estado inventado).
  const [roadmapBloqueado, setRoadmapBloqueado] = useState<boolean | null>(null);
  const [roadmapBloqueoSaving, setRoadmapBloqueoSaving] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    nombre: '', apellido: '', fecha_nacimiento: '',
    direccion: '', comuna: '', ciudad: '',
    contacto_emergencia_nombre: '', contacto_emergencia_telefono: '', contacto_emergencia_relacion: '',
    telefono: '', prevision: '', alergias: '', medicamentos_actuales: '', condiciones_medicas: '',
    tarifa_diferenciada: '', tarifa_diferenciada_nota: '', nombre_pareja: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState(false);
  const [editSectionOpen, setEditSectionOpen] = useState({ personal: true, clinico: true });
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [exportingSessionId, setExportingSessionId] = useState<string | null>(null);
  const [exportingFullRecord, setExportingFullRecord] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);

  async function handleExportSession(sesion: SessionRecord) {
    if (!record) return;
    setExportError(null);
    // Abrir la ventana ANTES de cualquier await — en iOS Safari (y popup
    // blockers en general) window.open() solo pasa si corre sincrónico
    // dentro del gesto de click. Ver comentario en ExportSessionParams.win.
    const win = window.open('', '_blank');
    if (!win) {
      setExportError('Habilitá las ventanas emergentes para exportar el PDF.');
      return;
    }
    setExportingSessionId(sesion.id);
    try {
      const { exportSessionPdf } = await import('../../lib/clinicalExport');
      // Mismo criterio DESC que usa fullRecordExport.ts internamente:
      // record.sesiones viene ordenado DESC (más reciente primero) desde la
      // RPC, así que la sesión más nueva es la N° más alto.
      const sessionNumber = record.sesiones.length - record.sesiones.findIndex(s => s.id === sesion.id);
      await exportSessionPdf({ paciente: record.paciente, sesion, callingTherapist, adminMode, sessionNumber, win });
    } catch {
      win.close();
      setExportError('No se pudo generar el PDF. Intentá nuevamente.');
    } finally {
      setExportingSessionId(null);
    }
  }

  async function handleExportFullRecord() {
    if (!record) return;
    setExportError(null);
    const win = window.open('', '_blank');
    if (!win) {
      setExportError('Habilitá las ventanas emergentes para exportar el PDF.');
      return;
    }
    setExportingFullRecord(true);
    try {
      const { exportFullRecordPdf } = await import('../../lib/clinicalExport');
      await exportFullRecordPdf({ paciente: record.paciente, sesiones: record.sesiones, callingTherapist, adminMode, win });
    } catch {
      win.close();
      setExportError('No se pudo generar el PDF. Intentá nuevamente.');
    } finally {
      setExportingFullRecord(false);
    }
  }

  useEffect(() => {
    async function loadRecord() {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = adminMode
        ? await supabase.rpc('get_patient_clinical_record_admin', { p_patient_id: patient.id })
        : await supabase.rpc('get_patient_clinical_record', {
            p_patient_id: patient.id,
            p_therapist_id: therapistId,
          });
      if (rpcError) {
        setError('No se pudo cargar la ficha clínica.');
      } else if (data) {
        const p = (data as any).paciente ?? {};
        const roadmapData = (data as any).roadmap ?? null;
        setRecord({
          paciente: { ...patient, ...p },
          sesiones: (data as any).sesiones ?? [],
          roadmap: roadmapData,
        });
        setStatusValue(normalizeEstadoRelacion(p.estado_relacion ?? patient.estado_relacion));
        setStatusError(null);
        setRoadmapSesion(String(roadmapData?.pareja?.sesion_actual ?? 1));
        setRoadmapError(null);
        setEditData({
          nombre: p.nombre ?? patient.nombre ?? '',
          apellido: p.apellido ?? patient.apellido ?? '',
          fecha_nacimiento: p.fecha_nacimiento ? p.fecha_nacimiento.slice(0, 10) : '',
          direccion: p.direccion ?? '',
          comuna: p.comuna ?? '',
          ciudad: p.ciudad ?? '',
          contacto_emergencia_nombre: p.contacto_emergencia_nombre ?? '',
          contacto_emergencia_telefono: p.contacto_emergencia_telefono ?? '',
          contacto_emergencia_relacion: p.contacto_emergencia_relacion ?? '',
          telefono: p.telefono ?? patient.telefono ?? '',
          prevision: p.prevision ?? patient.prevision ?? '',
          alergias: p.alergias ?? '',
          medicamentos_actuales: p.medicamentos_actuales ?? '',
          condiciones_medicas: p.condiciones_medicas ?? '',
          tarifa_diferenciada: p.tarifa_diferenciada != null ? String(p.tarifa_diferenciada) : '',
          tarifa_diferenciada_nota: p.tarifa_diferenciada_nota ?? '',
          nombre_pareja: p.nombre_pareja ?? '',
        });
      }
      setLoading(false);
    }
    loadRecord();
  }, [patient.id, therapistId, adminMode, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // FUNC-129 — estado del bloqueo del mapa. Solo se consulta si la ficha
  // muestra el control de la ruta (roadmap.pareja no-null).
  const hasRoadmapPareja = Boolean(record?.roadmap?.pareja);
  useEffect(() => {
    if (!hasRoadmapPareja) {
      setRoadmapBloqueado(null);
      return;
    }
    let cancelled = false;
    getRoadmapBloqueo(patient.id, adminMode ? null : therapistId, 'pareja').then((bloqueado) => {
      if (!cancelled) setRoadmapBloqueado(bloqueado);
    });
    return () => {
      cancelled = true;
    };
  }, [hasRoadmapPareja, patient.id, therapistId, adminMode]);

  function toggleSession(id: string) {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleEditSave() {
    setEditLoading(true);
    setEditError(null);
    const tarifaNum = editData.tarifa_diferenciada.trim()
      ? Number(editData.tarifa_diferenciada.replace(/\./g, '').replace(',', '.'))
      : null;
    const tarifaValida = (tarifaNum != null && !isNaN(tarifaNum)) ? tarifaNum : null;
    const tarifaNota = editData.tarifa_diferenciada_nota.trim() || null;
    const { error: rpcError } = await supabase.rpc('update_patient_basic_info', {
      p_patient_id: patient.id,
      p_therapist_id: therapistId,
      p_nombre: editData.nombre.trim() || null,
      p_apellido: editData.apellido.trim() || null,
      p_fecha_nacimiento: editData.fecha_nacimiento || null,
      p_telefono: editData.telefono || null,
      p_direccion: editData.direccion || null,
      p_comuna: editData.comuna || null,
      p_ciudad: editData.ciudad || null,
      p_cont_emerg_nombre: editData.contacto_emergencia_nombre || null,
      p_cont_emerg_telefono: editData.contacto_emergencia_telefono || null,
      p_cont_emerg_relacion: editData.contacto_emergencia_relacion || null,
      p_prevision: editData.prevision || null,
      p_alergias: editData.alergias || null,
      p_medicamentos: editData.medicamentos_actuales || null,
      p_condiciones: editData.condiciones_medicas || null,
      p_tarifa_diferenciada: tarifaValida,
      p_tarifa_diferenciada_nota: tarifaNota,
      p_nombre_pareja: editData.nombre_pareja.trim() || null,
    });
    if (rpcError) {
      setEditError('No se pudo guardar los cambios. Intentá nuevamente.');
    } else {
      setRecord(prev => prev ? {
        ...prev,
        paciente: {
          ...prev.paciente,
          ...editData,
          tarifa_diferenciada: tarifaValida,
          tarifa_diferenciada_nota: tarifaNota,
        }
      } : null);
      setEditMode(false);
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 2500);
    }
    setEditLoading(false);
  }

  async function handleStatusSave() {
    if (!record) return;
    const nuevo = statusValue;
    if (nuevo === record.paciente.estado_relacion) return;
    setStatusSaving(true);
    setStatusError(null);
    const { error: rpcError } = await supabase.rpc('set_patient_relationship_status', {
      p_patient_id: patient.id,
      p_therapist_id: adminMode ? null : therapistId,
      p_estado: nuevo,
    });
    if (rpcError) {
      setStatusError('No se pudo cambiar el estado. Intentá nuevamente.');
    } else {
      setRecord(prev => prev ? { ...prev, paciente: { ...prev.paciente, estado_relacion: nuevo } } : null);
      onPatientUpdated?.(patient.id, { estado_relacion: nuevo });
      setStatusSaved(true);
      setTimeout(() => setStatusSaved(false), 2500);
    }
    setStatusSaving(false);
  }

  // Roadmap de pareja — plan §7.8. `nuevaSesion = null` vuelve a modo automático
  // (borra el override server-side). Mismo patrón de ownership que handleStatusSave:
  // adminMode → p_therapist_id NULL (rama director en la RPC); si no, el terapeuta
  // dueño real (paridad director/terapeuta, [[feedback_director_therapist_parity]]).
  async function handleRoadmapSave(nuevaSesion: number | null) {
    if (!record) return;
    setRoadmapSaving(true);
    setRoadmapError(null);
    const result = await setRoadmapProgreso(
      patient.id,
      adminMode ? null : therapistId,
      'pareja',
      nuevaSesion
    );
    if (!result.ok) {
      setRoadmapError('No se pudo actualizar la ruta de pareja. Intentá nuevamente.');
      setRoadmapSaving(false);
      return;
    }
    const sesionFinal = result.data?.sesion_actual ?? nuevaSesion ?? 0;
    const modoFinal: 'auto' | 'manual' = nuevaSesion === null ? 'auto' : 'manual';
    setRecord((prev) =>
      prev
        ? {
            ...prev,
            roadmap: { pareja: { sesion_actual: sesionFinal, modo: modoFinal, total: 12 } },
          }
        : null
    );
    setRoadmapSesion(String(sesionFinal || 1));
    setRoadmapSaved(true);
    setTimeout(() => setRoadmapSaved(false), 2500);
    setRoadmapSaving(false);
  }

  async function handleRoadmapBloqueoToggle(nextBloqueado: boolean) {
    setRoadmapBloqueoSaving(true);
    setRoadmapError(null);
    const ok = await setRoadmapBloqueo(patient.id, adminMode ? null : therapistId, 'pareja', nextBloqueado);
    if (ok) {
      setRoadmapBloqueado(nextBloqueado);
      setRoadmapSaved(true);
      setTimeout(() => setRoadmapSaved(false), 2500);
    } else {
      setRoadmapError('No se pudo actualizar la visibilidad del mapa. Intentá nuevamente.');
    }
    setRoadmapBloqueoSaving(false);
  }

  async function handleBlockToggle(nextBlocked: boolean) {
    if (!record) return;
    setBlockSaving(true);
    setBlockError(null);
    const { error: rpcError } = await supabase.rpc('set_patient_clinic_block', {
      p_patient_id: patient.id,
      p_blocked: nextBlocked,
      p_motivo: nextBlocked ? (blockMotivo.trim() || null) : null,
    });
    if (rpcError) {
      setBlockError(
        nextBlocked
          ? 'No se pudo bloquear al paciente. Intentá nuevamente.'
          : 'No se pudo desbloquear al paciente. Intentá nuevamente.'
      );
      setBlockSaving(false);
      return;
    }
    setBlockSaving(false);
    setBlockPanelOpen(false);
    setBlockMotivo('');
    onPatientUpdated?.(patient.id, { bloqueado_clinica: nextBlocked });
    // Recargar: bloquear canceló consultas futuras server-side, la lista de
    // sesiones de la ficha tiene que reflejarlo.
    setReloadNonce((n) => n + 1);
  }

  if (loading) {
    return (
      <div className="cr-record-view">
        <button className="cr-back-btn" onClick={onBack} aria-label="Volver a la lista">
          <i className="fas fa-arrow-left" aria-hidden="true"></i>
          Volver
        </button>
        <RecordSkeleton />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="cr-record-view">
        <button className="cr-back-btn" onClick={onBack} aria-label="Volver a la lista">
          <i className="fas fa-arrow-left" aria-hidden="true"></i>
          Volver
        </button>
        <div className="cr-empty-state">
          <i className="fas fa-exclamation-circle cr-empty-state__icon" aria-hidden="true"></i>
          <p className="cr-empty-state__body">{error ?? 'No se pudo cargar la ficha.'}</p>
        </div>
      </div>
    );
  }

  const { paciente, sesiones } = record;

  return (
    <div className="cr-record-view">
      {/* Botón volver */}
      <button className="cr-back-btn" onClick={onBack} aria-label="Volver a la lista">
        <i className="fas fa-arrow-left" aria-hidden="true"></i>
        Volver
      </button>

      {/* Header del paciente */}
      <div className="cr-record-header cr-record-header--with-actions">
        <div className="cr-record-header__identity">
          <span
            className="patient-avatar-large patient-avatar--initials"
            aria-hidden="true"
          >
            {getInitials(paciente.nombre, paciente.apellido)}
          </span>
          <div className="cr-record-header__details">
            <h1 className="cr-record-name">
              {paciente.nombre} {paciente.apellido}
            </h1>
            <p className="cr-record-rut">{paciente.rut}</p>
            <div className="cr-record-badges">
              <RelationshipStatusBadge estado={normalizeEstadoRelacion(paciente.estado_relacion)} activo={paciente.activo} />
              {paciente.bloqueado_clinica && <BlockedBadge />}
              {paciente.prevision && (
                <span className="cr-badge cr-badge--neutral">
                  {labelPrevision(paciente.prevision)}
                </span>
              )}
            </div>
            {paciente.activo && (
              <div className="cr-status-control">
                <label className="cr-status-control__label" htmlFor="cr-estado-relacion">
                  Estado de la relación
                </label>
                <div className="cr-status-control__row">
                  <select
                    id="cr-estado-relacion"
                    className="cr-edit-input cr-status-control__select"
                    value={statusValue}
                    disabled={statusSaving}
                    onChange={(e) => { setStatusValue(e.target.value as EstadoRelacion); setStatusError(null); }}
                  >
                    {ESTADOS_RELACION.map((e) => (
                      <option key={e} value={e}>{RELACION_META[e].label}</option>
                    ))}
                  </select>
                  {statusValue !== paciente.estado_relacion && (
                    <button
                      type="button"
                      className="cr-btn cr-btn--primary cr-status-control__save"
                      onClick={handleStatusSave}
                      disabled={statusSaving}
                    >
                      {statusSaving ? 'Guardando…' : 'Guardar estado'}
                    </button>
                  )}
                  {statusSaved && (
                    <span className="cr-status-control__ok">
                      <i className="fas fa-check-circle" aria-hidden="true"></i> Guardado
                    </span>
                  )}
                </div>
                {statusError && (
                  <span className="cr-status-control__error" role="alert">{statusError}</span>
                )}
              </div>
            )}

            {/* Ruta de pareja — plan §7.8. Solo visible si el paciente tiene ≥1
                consulta 'pareja' u override (roadmap.pareja no-null en el jsonb
                de get_patient_clinical_record[_admin]). Mismo patrón visual que
                cr-status-control, junto a él. */}
            {record.roadmap?.pareja && (
              <div className="cr-roadmap-control">
                <label className="cr-roadmap-control__label" htmlFor="cr-roadmap-sesion">
                  Ruta de pareja — sesión {record.roadmap.pareja.sesion_actual} de{' '}
                  {record.roadmap.pareja.total}{' '}
                  <span
                    className={`cr-badge ${
                      record.roadmap.pareja.modo === 'manual' ? 'cr-badge--orange' : 'cr-badge--neutral'
                    }`}
                  >
                    {record.roadmap.pareja.modo === 'manual' ? 'Manual' : 'Automático'}
                  </span>
                </label>
                <div className="cr-roadmap-control__row">
                  <select
                    id="cr-roadmap-sesion"
                    className="cr-edit-input cr-roadmap-control__select"
                    value={roadmapSesion}
                    disabled={roadmapSaving}
                    onChange={(e) => {
                      setRoadmapSesion(e.target.value);
                      setRoadmapError(null);
                    }}
                  >
                    {Array.from({ length: record.roadmap.pareja.total }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="cr-btn cr-btn--primary cr-roadmap-control__save"
                    onClick={() => handleRoadmapSave(Number(roadmapSesion))}
                    disabled={roadmapSaving}
                  >
                    {roadmapSaving ? 'Guardando…' : 'Fijar sesión'}
                  </button>
                  {record.roadmap.pareja.modo === 'manual' && (
                    <button
                      type="button"
                      className="cr-roadmap-control__auto"
                      onClick={() => handleRoadmapSave(null)}
                      disabled={roadmapSaving}
                    >
                      Volver a automático
                    </button>
                  )}
                  {roadmapSaved && (
                    <span className="cr-status-control__ok">
                      <i className="fas fa-check-circle" aria-hidden="true"></i> Guardado
                    </span>
                  )}
                </div>
                {/* FUNC-129 — ocultar el mapa en /agendar (ej. pareja que
                    terminó o paciente inactivo). No borra el progreso. */}
                <label className="cr-roadmap-control__toggle">
                  <input
                    type="checkbox"
                    checked={roadmapBloqueado === true}
                    disabled={roadmapBloqueado === null || roadmapBloqueoSaving}
                    onChange={(e) => handleRoadmapBloqueoToggle(e.target.checked)}
                  />
                  <span>
                    Ocultar el mapa al paciente
                    {roadmapBloqueado === true && (
                      <span className="cr-badge cr-badge--orange cr-roadmap-control__toggle-badge">Oculto</span>
                    )}
                  </span>
                </label>
                {roadmapError && (
                  <span className="cr-status-control__error" role="alert">{roadmapError}</span>
                )}
              </div>
            )}

            {/* Bloqueo administrativo — sólo director. Cancela las citas futuras
                del paciente y le corta el agendamiento por todos los canales. */}
            {adminMode && paciente.activo && (
              <div className="cr-block-control">
                {paciente.bloqueado_clinica ? (
                  <>
                    <p className="cr-block-control__status">
                      <i className="fas fa-ban" aria-hidden="true"></i>
                      Bloqueado para agendar
                      {paciente.bloqueo_at && (
                        <span className="cr-block-control__meta"> · {formatShortDate(paciente.bloqueo_at)}</span>
                      )}
                    </p>
                    {paciente.bloqueo_motivo && (
                      <p className="cr-block-control__motivo">Motivo: {paciente.bloqueo_motivo}</p>
                    )}
                    <button
                      type="button"
                      className="cr-btn cr-btn--outline cr-block-control__btn"
                      onClick={() => handleBlockToggle(false)}
                      disabled={blockSaving}
                    >
                      {blockSaving ? 'Procesando…' : 'Desbloquear paciente'}
                    </button>
                  </>
                ) : blockPanelOpen ? (
                  <>
                    <label className="cr-block-control__label" htmlFor="cr-block-motivo">
                      Motivo del bloqueo (interno — no lo ve el paciente)
                    </label>
                    <textarea
                      id="cr-block-motivo"
                      className="cr-edit-input cr-block-control__textarea"
                      rows={2}
                      value={blockMotivo}
                      disabled={blockSaving}
                      onChange={(e) => { setBlockMotivo(e.target.value); setBlockError(null); }}
                    />
                    <p className="cr-block-control__warning" role="note">
                      Se cancelarán todas sus citas futuras y no podrá volver a agendar.
                    </p>
                    <div className="cr-block-control__actions">
                      <button
                        type="button"
                        className="cr-btn cr-btn--danger cr-block-control__btn"
                        onClick={() => handleBlockToggle(true)}
                        disabled={blockSaving}
                      >
                        {blockSaving ? 'Bloqueando…' : 'Confirmar bloqueo'}
                      </button>
                      <button
                        type="button"
                        className="cr-btn cr-btn--outline cr-block-control__btn"
                        onClick={() => { setBlockPanelOpen(false); setBlockMotivo(''); setBlockError(null); }}
                        disabled={blockSaving}
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="cr-btn cr-btn--outline cr-block-control__btn cr-block-control__btn--danger-text"
                    onClick={() => setBlockPanelOpen(true)}
                  >
                    <i className="fas fa-ban" aria-hidden="true"></i>
                    Bloquear paciente
                  </button>
                )}
                {blockError && (
                  <span className="cr-status-control__error" role="alert">{blockError}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="cr-record-header__actions">
          {editSuccess && (
            <span className="cr-edit-success-msg">
              <i className="fas fa-check-circle" aria-hidden="true"></i>
              Guardado
            </span>
          )}
          {onSchedule && (
            <button className="cr-btn cr-btn--primary" onClick={() => onSchedule(paciente)}>
              <i className="fas fa-calendar-plus" aria-hidden="true"></i>
              Agendar cita
            </button>
          )}
          <button
            className="cr-btn cr-btn--outline"
            onClick={handleExportFullRecord}
            disabled={exportingFullRecord}
            title="Exportar ficha completa en PDF (Ley 20.584)"
          >
            <i className={`fas fa-${exportingFullRecord ? 'spinner fa-spin' : 'file-download'}`} aria-hidden="true"></i>
            {exportingFullRecord ? 'Generando…' : 'Exportar ficha completa'}
          </button>
          {!adminMode && (
            <button
              className="cr-btn cr-btn--outline"
              onClick={() => { setEditMode(v => !v); setEditError(null); }}
            >
              <i className={`fas fa-${editMode ? 'times' : 'pencil-alt'}`} aria-hidden="true"></i>
              {editMode ? 'Cancelar' : 'Editar'}
            </button>
          )}
          {!adminMode && (
            <button
              className="cr-btn cr-btn--outline"
              onClick={() => setShowCertificateModal(true)}
            >
              <i className="fas fa-certificate" aria-hidden="true"></i>
              Generar certificado
            </button>
          )}
        </div>
      </div>
      {exportError && <p className="cr-edit-error" role="alert">{exportError}</p>}

      {/* Quick stats row */}
      <div className="cr-quick-stats">
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">{paciente.total_sesiones}</span>
          <span className="cr-quick-stat__label">Sesiones totales</span>
        </div>
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">
            {formatShortDate(paciente.ultima_sesion)}
          </span>
          <span className="cr-quick-stat__label">Última sesión</span>
        </div>
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">
            {paciente.proxima_sesion
              ? formatShortDate(paciente.proxima_sesion)
              : 'Sin agendar'}
          </span>
          <span className="cr-quick-stat__label">Próxima sesión</span>
        </div>
        <div className="cr-quick-stat">
          <span className="cr-quick-stat__value">
            {labelPrevision(paciente.prevision)}
          </span>
          <span className="cr-quick-stat__label">Previsión</span>
        </div>
      </div>

      {/* Historia clínica — línea de tiempo condensada (§2.2) */}
      <ClinicalTimeline sesiones={sesiones} activo={paciente.activo && statusValue === 'activo'} />

      {/* Grid de información */}
      <div className="cr-info-grid">
        {/* Datos personales */}
        <section className="cr-info-card" aria-labelledby="cr-personal-title">
          <h2 className="cr-info-card__title" id="cr-personal-title">
            <i className="fas fa-user" aria-hidden="true"></i>
            Datos Personales
          </h2>
          <dl className="cr-info-list">
            <div className="cr-info-row">
              <dt>Nombre completo</dt>
              <dd>{paciente.nombre} {paciente.apellido}</dd>
            </div>
            <div className="cr-info-row">
              <dt>Email</dt>
              <dd>{paciente.email}</dd>
            </div>
            {paciente.telefono && (
              <div className="cr-info-row">
                <dt>Teléfono</dt>
                <dd>{paciente.telefono}</dd>
              </div>
            )}
            {paciente.fecha_nacimiento && (
              <div className="cr-info-row">
                <dt>Fecha de nacimiento</dt>
                <dd>
                  {formatDate(paciente.fecha_nacimiento)}{' '}
                  <span className="cr-text-muted">
                    ({calcAge(paciente.fecha_nacimiento)})
                  </span>
                </dd>
              </div>
            )}
            {(paciente.direccion || paciente.comuna) && (
              <div className="cr-info-row">
                <dt>Dirección</dt>
                <dd>
                  {[paciente.direccion, paciente.comuna, paciente.ciudad]
                    .filter(Boolean)
                    .join(', ')}
                </dd>
              </div>
            )}
            {paciente.nombre_pareja && (
              <div className="cr-info-row">
                <dt>Nombre de la pareja</dt>
                <dd>{paciente.nombre_pareja}</dd>
              </div>
            )}
          </dl>
        </section>

        {/* Previsión y seguro */}
        <section className="cr-info-card" aria-labelledby="cr-prevision-title">
          <h2 className="cr-info-card__title" id="cr-prevision-title">
            <i className="fas fa-shield-alt" aria-hidden="true"></i>
            Previsión y Seguro
          </h2>
          <dl className="cr-info-list">
            <div className="cr-info-row">
              <dt>Previsión</dt>
              <dd>{labelPrevision(paciente.prevision)}</dd>
            </div>
            {paciente.nombre_isapre && (
              <div className="cr-info-row">
                <dt>Isapre</dt>
                <dd>{paciente.nombre_isapre}</dd>
              </div>
            )}
            {paciente.numero_beneficiario && (
              <div className="cr-info-row">
                <dt>N° beneficiario</dt>
                <dd className="cr-text-mono">{paciente.numero_beneficiario}</dd>
              </div>
            )}
          </dl>
        </section>

        {/* Tarifa diferenciada */}
        {paciente.tarifa_diferenciada != null && (
          <section className="cr-info-card" aria-labelledby="cr-tarifa-title">
            <h2 className="cr-info-card__title" id="cr-tarifa-title">
              <i className="fas fa-tag" aria-hidden="true"></i>
              Tarifa Personalizada
            </h2>
            <dl className="cr-info-list">
              <div className="cr-info-row">
                <dt>Valor por sesión</dt>
                <dd className="cr-tarifa-value">
                  ${paciente.tarifa_diferenciada.toLocaleString('es-CL')}
                </dd>
              </div>
              {paciente.tarifa_diferenciada_nota && (
                <div className="cr-info-row">
                  <dt>Motivo</dt>
                  <dd>{paciente.tarifa_diferenciada_nota}</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {/* Contacto de emergencia */}
        {(paciente.contacto_emergencia_nombre ||
          paciente.contacto_emergencia_telefono) && (
          <section className="cr-info-card" aria-labelledby="cr-emergency-title">
            <h2 className="cr-info-card__title" id="cr-emergency-title">
              <i className="fas fa-phone-alt" aria-hidden="true"></i>
              Contacto de Emergencia
            </h2>
            <dl className="cr-info-list">
              {paciente.contacto_emergencia_nombre && (
                <div className="cr-info-row">
                  <dt>Nombre</dt>
                  <dd>{paciente.contacto_emergencia_nombre}</dd>
                </div>
              )}
              {paciente.contacto_emergencia_telefono && (
                <div className="cr-info-row">
                  <dt>Teléfono</dt>
                  <dd>{paciente.contacto_emergencia_telefono}</dd>
                </div>
              )}
              {paciente.contacto_emergencia_relacion && (
                <div className="cr-info-row">
                  <dt>Relación</dt>
                  <dd>{paciente.contacto_emergencia_relacion}</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {/* Antecedentes médicos */}
        {(paciente.alergias ||
          paciente.medicamentos_actuales ||
          paciente.condiciones_medicas) && (
          <section className="cr-info-card" aria-labelledby="cr-medical-title">
            <h2 className="cr-info-card__title" id="cr-medical-title">
              <i className="fas fa-notes-medical" aria-hidden="true"></i>
              Antecedentes Médicos
            </h2>
            <dl className="cr-info-list">
              {paciente.alergias && (
                <div className="cr-info-row">
                  <dt>
                    <i className="fas fa-exclamation-triangle cr-icon-warning" aria-hidden="true"></i>{' '}
                    Alergias
                  </dt>
                  <dd>{paciente.alergias}</dd>
                </div>
              )}
              {paciente.medicamentos_actuales && (
                <div className="cr-info-row">
                  <dt>
                    <i className="fas fa-pills cr-icon-blue" aria-hidden="true"></i>{' '}
                    Medicamentos
                  </dt>
                  <dd>{paciente.medicamentos_actuales}</dd>
                </div>
              )}
              {paciente.condiciones_medicas && (
                <div className="cr-info-row">
                  <dt>
                    <i className="fas fa-heartbeat cr-icon-muted" aria-hidden="true"></i>{' '}
                    Condiciones
                  </dt>
                  <dd>{paciente.condiciones_medicas}</dd>
                </div>
              )}
            </dl>
          </section>
        )}
      </div>

      {/* Formulario de edición inline */}
      {editMode && !adminMode && (
        <section className="cr-edit-section" aria-label="Editar información del paciente">
          <h2 className="cr-edit-section__title">
            <i className="fas fa-pencil-alt" aria-hidden="true"></i>
            Editar información
          </h2>
          {editError && <p className="cr-edit-error" role="alert">{editError}</p>}

          {/* Sub-sección: Datos personales */}
          <button
            type="button"
            className="cr-session-expand-btn"
            onClick={() => setEditSectionOpen(s => ({ ...s, personal: !s.personal }))}
            aria-expanded={editSectionOpen.personal}
            aria-controls="edit-section-personal"
          >
            Datos personales
            <i className={`fas fa-chevron-${editSectionOpen.personal ? 'up' : 'down'}`} aria-hidden="true"></i>
          </button>
          {editSectionOpen.personal && (
            <div className="cr-edit-grid" id="edit-section-personal">
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-nombre">Nombre</label>
                <input
                  id="edit-nombre"
                  className="cr-edit-input"
                  type="text"
                  value={editData.nombre}
                  onChange={e => setEditData(d => ({ ...d, nombre: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-apellido">Apellido</label>
                <input
                  id="edit-apellido"
                  className="cr-edit-input"
                  type="text"
                  value={editData.apellido}
                  onChange={e => setEditData(d => ({ ...d, apellido: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-fecha-nacimiento">Fecha de nacimiento</label>
                <input
                  id="edit-fecha-nacimiento"
                  className="cr-edit-input"
                  type="date"
                  value={editData.fecha_nacimiento}
                  onChange={e => setEditData(d => ({ ...d, fecha_nacimiento: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-telefono">Teléfono</label>
                <input
                  id="edit-telefono"
                  className="cr-edit-input"
                  type="tel"
                  value={editData.telefono}
                  onChange={e => setEditData(d => ({ ...d, telefono: e.target.value }))}
                  placeholder="+56 9 XXXX XXXX"
                />
              </div>
              <div className="cr-edit-field cr-edit-field--full">
                <label className="cr-edit-label" htmlFor="edit-direccion">Dirección</label>
                <input
                  id="edit-direccion"
                  className="cr-edit-input"
                  type="text"
                  value={editData.direccion}
                  onChange={e => setEditData(d => ({ ...d, direccion: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-comuna">Comuna</label>
                <input
                  id="edit-comuna"
                  className="cr-edit-input"
                  type="text"
                  value={editData.comuna}
                  onChange={e => setEditData(d => ({ ...d, comuna: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-ciudad">Ciudad</label>
                <input
                  id="edit-ciudad"
                  className="cr-edit-input"
                  type="text"
                  value={editData.ciudad}
                  onChange={e => setEditData(d => ({ ...d, ciudad: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field cr-edit-field--full">
                <label className="cr-edit-label" htmlFor="edit-nombre-pareja">Nombre de la pareja</label>
                <input
                  id="edit-nombre-pareja"
                  className="cr-edit-input"
                  type="text"
                  value={editData.nombre_pareja}
                  onChange={e => setEditData(d => ({ ...d, nombre_pareja: e.target.value }))}
                  placeholder="Nombre completo (terapia de pareja/familiar)"
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-cont-emerg-nombre">Contacto emergencia — Nombre</label>
                <input
                  id="edit-cont-emerg-nombre"
                  className="cr-edit-input"
                  type="text"
                  value={editData.contacto_emergencia_nombre}
                  onChange={e => setEditData(d => ({ ...d, contacto_emergencia_nombre: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-cont-emerg-telefono">Contacto emergencia — Teléfono</label>
                <input
                  id="edit-cont-emerg-telefono"
                  className="cr-edit-input"
                  type="tel"
                  value={editData.contacto_emergencia_telefono}
                  onChange={e => setEditData(d => ({ ...d, contacto_emergencia_telefono: e.target.value }))}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-cont-emerg-relacion">Contacto emergencia — Relación</label>
                <input
                  id="edit-cont-emerg-relacion"
                  className="cr-edit-input"
                  type="text"
                  value={editData.contacto_emergencia_relacion}
                  onChange={e => setEditData(d => ({ ...d, contacto_emergencia_relacion: e.target.value }))}
                  placeholder="Ej: Madre, Pareja, Hermano…"
                />
              </div>
            </div>
          )}

          {/* Sub-sección: Información clínica */}
          <button
            type="button"
            className="cr-session-expand-btn"
            onClick={() => setEditSectionOpen(s => ({ ...s, clinico: !s.clinico }))}
            aria-expanded={editSectionOpen.clinico}
            aria-controls="edit-section-clinico"
          >
            Información clínica
            <i className={`fas fa-chevron-${editSectionOpen.clinico ? 'up' : 'down'}`} aria-hidden="true"></i>
          </button>
          {editSectionOpen.clinico && (
            <div className="cr-edit-grid" id="edit-section-clinico">
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-prevision">Previsión</label>
                <select
                  id="edit-prevision"
                  className="cr-edit-input"
                  value={editData.prevision}
                  onChange={e => setEditData(d => ({ ...d, prevision: e.target.value }))}
                >
                  <option value="">Sin información</option>
                  <option value="particular">Particular</option>
                  <option value="fonasa">Fonasa</option>
                  <option value="isapre">Isapre</option>
                </select>
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-alergias">Alergias</label>
                <textarea
                  id="edit-alergias"
                  className="cr-edit-input cr-edit-input--textarea"
                  rows={2}
                  value={editData.alergias}
                  onChange={e => setEditData(d => ({ ...d, alergias: e.target.value }))}
                  placeholder="Sin alergias conocidas"
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-medicamentos">Medicamentos actuales</label>
                <textarea
                  id="edit-medicamentos"
                  className="cr-edit-input cr-edit-input--textarea"
                  rows={2}
                  value={editData.medicamentos_actuales}
                  onChange={e => setEditData(d => ({ ...d, medicamentos_actuales: e.target.value }))}
                  placeholder="Sin medicamentos"
                />
              </div>
              <div className="cr-edit-field cr-edit-field--full">
                <label className="cr-edit-label" htmlFor="edit-condiciones">Condiciones médicas</label>
                <textarea
                  id="edit-condiciones"
                  className="cr-edit-input cr-edit-input--textarea"
                  rows={2}
                  value={editData.condiciones_medicas}
                  onChange={e => setEditData(d => ({ ...d, condiciones_medicas: e.target.value }))}
                  placeholder="Sin condiciones registradas"
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-tarifa">Tarifa personalizada ($)</label>
                <input
                  id="edit-tarifa"
                  className="cr-edit-input"
                  type="number"
                  min="0"
                  step="500"
                  value={editData.tarifa_diferenciada}
                  onChange={e => setEditData(d => ({ ...d, tarifa_diferenciada: e.target.value }))}
                  placeholder="Dejar vacío = tarifa estándar"
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="edit-tarifa-nota">Motivo del acuerdo</label>
                <input
                  id="edit-tarifa-nota"
                  className="cr-edit-input"
                  type="text"
                  value={editData.tarifa_diferenciada_nota}
                  onChange={e => setEditData(d => ({ ...d, tarifa_diferenciada_nota: e.target.value }))}
                  placeholder="Ej: Convenio familiar, Becado…"
                />
              </div>
            </div>
          )}

          <div className="cr-edit-actions">
            <button className="cr-btn cr-btn--primary" onClick={handleEditSave} disabled={editLoading}>
              {editLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button className="cr-btn cr-btn--ghost" onClick={() => { setEditMode(false); setEditError(null); }}>
              Cancelar
            </button>
          </div>
        </section>
      )}

      {/* Historial de sesiones */}
      <section className="cr-sessions-section" aria-labelledby="cr-sessions-title">
        <div className="cr-sessions-header">
          <h2 className="cr-sessions-title" id="cr-sessions-title">
            Historial de Sesiones
          </h2>
          <span className="cr-sessions-count-badge">{sesiones.length}</span>
        </div>

        {sesiones.length === 0 ? (
          <div className="cr-empty-state cr-empty-state--inline">
            <p className="cr-empty-state__body">No hay sesiones registradas.</p>
          </div>
        ) : (
          <ul className="cr-sessions-list">
            {sesiones.map((sesion) => {
              const isExpanded = expandedSessions.has(sesion.id);
              // Editor reducido (2026-09): el "Registro clínico de la sesión"
              // son 4 observaciones mapeadas a columnas reales de `consultas`.
              const observaciones = [
                { label: 'Observación I', text: sesion.observacion_clinica },
                { label: 'Observación II', text: sesion.desarrollo_sesion },
                { label: 'Observación III', text: sesion.respuesta_evolucion },
                { label: 'Observación IV', text: sesion.observaciones_adicionales },
              ].filter((o) => o.text?.trim());
              const tieneRegistro = observaciones.length > 0;
              return (
                <li key={sesion.id} className="cr-session-item">
                  {/* Fila principal */}
                  <div className="cr-session-row">
                    <div className="cr-session-date-block">
                      <span className="cr-session-date">
                        {formatShortDate(sesion.fecha_hora)}
                      </span>
                      <span className="cr-session-time">
                        {new Date(sesion.fecha_hora).toLocaleTimeString('es-CL', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {' · '}{sesion.duracion} min
                      </span>
                    </div>

                    <div className="cr-session-meta">
                      <span className="cr-session-tipo">{sesion.tipo_consulta}</span>
                      <span className="cr-session-modalidad">
                        <i
                          className={`fas fa-${sesion.modalidad === 'online' ? 'video' : 'building'}`}
                          aria-hidden="true"
                        ></i>{' '}
                        {sesion.modalidad}
                      </span>
                    </div>

                    <SessionStatusBadge estado={sesion.estado} />

                    {sesion.motivo_consulta && (
                      <p className="cr-session-motivo" title={sesion.motivo_consulta}>
                        {sesion.motivo_consulta}
                      </p>
                    )}

                    {tieneRegistro && editingSessionId !== sesion.id && (
                      <button
                        className="cr-session-expand-btn"
                        onClick={() => toggleSession(sesion.id)}
                        aria-expanded={isExpanded}
                        aria-controls={`session-notes-${sesion.id}`}
                      >
                        {isExpanded ? 'Ocultar registro' : 'Ver registro clínico'}
                        <i
                          className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}
                          aria-hidden="true"
                        ></i>
                      </button>
                    )}

                    {!adminMode && (
                      <>
                        <span className={`cr-badge ${tieneRegistro ? 'cr-badge--neutral' : 'cr-badge--muted'}`}>
                          {tieneRegistro ? 'Con registro' : 'Sin registro'}
                        </span>
                        <button
                          className="cr-session-expand-btn"
                          onClick={() => setEditingSessionId(id => id === sesion.id ? null : sesion.id)}
                          aria-expanded={editingSessionId === sesion.id}
                          aria-controls={`session-edit-${sesion.id}`}
                        >
                          {editingSessionId === sesion.id ? 'Cancelar edición' : 'Editar registro'}
                          <i className="fas fa-pencil-alt" aria-hidden="true"></i>
                        </button>
                      </>
                    )}

                    <button
                      className="cr-session-expand-btn"
                      onClick={() => handleExportSession(sesion)}
                      disabled={exportingSessionId === sesion.id}
                      title="Exportar esta sesión en PDF (Ley 20.584)"
                    >
                      {exportingSessionId === sesion.id ? 'Generando…' : 'Exportar PDF'}
                      <i className={`fas fa-${exportingSessionId === sesion.id ? 'spinner fa-spin' : 'file-pdf'}`} aria-hidden="true"></i>
                    </button>
                  </div>

                  {/* Editor inline de notas — solo una sesión a la vez, no en adminMode */}
                  {editingSessionId === sesion.id && !adminMode && (
                    <SessionNoteEditor
                      sesion={sesion}
                      sesiones={record.sesiones}
                      therapistId={therapistId}
                      patientId={patient.id}
                      onCancel={() => setEditingSessionId(null)}
                      onSaved={(updated) => {
                        setRecord(prev => prev ? {
                          ...prev,
                          sesiones: prev.sesiones.map(s => s.id === sesion.id ? { ...s, ...updated } : s),
                        } : null);
                        setEditingSessionId(null);
                        setExpandedSessions(prev => new Set(prev).add(sesion.id));
                      }}
                    />
                  )}

                  {/* Registro clínico expandible (solo lectura) */}
                  {isExpanded && editingSessionId !== sesion.id && (
                    <div
                      id={`session-notes-${sesion.id}`}
                      className="cr-session-notes"
                    >
                      {observaciones.map((o) => (
                        <div key={o.label} className="cr-session-notes__block">
                          <h4 className="cr-session-notes__label">
                            <i className="fas fa-pen" aria-hidden="true"></i>
                            {o.label}
                          </h4>
                          <p className="cr-session-notes__text">{o.text}</p>
                        </div>
                      ))}
                      {!tieneRegistro && (
                        <p className="cr-text-muted">Sin registro clínico para esta sesión.</p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {showCertificateModal && (
        <CertificateGenerator
          patientId={paciente.id}
          therapistId={therapistId}
          patientNombre={paciente.nombre}
          patientApellido={paciente.apellido}
          onClose={() => setShowCertificateModal(false)}
        />
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function ClinicalRecordsModule({
  therapist,
  adminMode = false,
  compactRegistration = false,
}: ClinicalRecordsModuleProps) {
  const [view, setView] = useState<'list' | 'record'>('list');
  const [selectedPatient, setSelectedPatient] = useState<PatientWithStats | null>(null);
  const [patients, setPatients] = useState<PatientWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showScheduler, setShowScheduler] = useState(false);
  const [schedulerPatient, setSchedulerPatient] = useState<PatientWithStats | null>(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [deleteConfirmPatient, setDeleteConfirmPatient] = useState<PatientWithStats | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleSchedulePatient = (patient: PatientWithStats) => {
    setSchedulerPatient(patient);
    setShowScheduler(true);
  };

  const handleDeleteRequest = (patient: PatientWithStats) => {
    setDeleteError(null);
    setDeleteConfirmPatient(patient);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmPatient) return;
    setDeleteLoading(true);
    setDeleteError(null);
    const { error: rpcError } = adminMode
      ? await supabase.rpc('delete_patient_director', {
          p_paciente_id: deleteConfirmPatient.id,
        })
      : await supabase.rpc('delete_patient_therapist', {
          p_paciente_id: deleteConfirmPatient.id,
          p_therapist_id: therapist.id,
        });
    if (rpcError) {
      setDeleteError('No se pudo eliminar el paciente. Intentá nuevamente.');
      setDeleteLoading(false);
    } else {
      setPatients(prev => prev.filter(p => p.id !== deleteConfirmPatient.id));
      setDeleteConfirmPatient(null);
      setDeleteLoading(false);
    }
  };

  const handlePatientRegistered = (newPatient: any) => {
    setShowRegistration(false);
    setPatients(prev => [{
      id: newPatient.id,
      nombre: newPatient.nombre,
      apellido: newPatient.apellido,
      email: newPatient.email,
      telefono: newPatient.telefono,
      rut: newPatient.rut,
      fecha_nacimiento: newPatient.fecha_nacimiento,
      activo: true,
      estado_relacion: 'activo',
      bloqueado_clinica: false,
      total_sesiones: 0,
    }, ...prev]);
  };

  useEffect(() => {
    async function loadPatients() {
      setLoading(true);
      setError(null);
      if (adminMode) {
        const { data, error: rpcError } = await supabase.rpc('get_all_patients_director');
        if (rpcError) {
          setError('No se pudo cargar la lista de pacientes.');
        } else {
          setPatients(
            (data ?? []).map((p: any) => ({
              ...p,
              estado_relacion: normalizeEstadoRelacion(p.estado_relacion),
              total_sesiones: Number(p.total_sesiones ?? 0),
            }))
          );
        }
      } else {
        const { data, error: rpcError } = await supabase.rpc('get_patient_list_for_therapist', {
          p_therapist_id: therapist.id
        });
        if (rpcError) {
          setError('No se pudo cargar la lista de pacientes.');
        } else {
          setPatients(
            (data ?? []).map((p: any) => ({
              ...p,
              estado_relacion: normalizeEstadoRelacion(p.estado_relacion),
              total_sesiones: Number(p.total_sesiones)
            }))
          );
        }
      }
      setLoading(false);
    }
    loadPatients();
  }, [therapist.id, adminMode]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSelectPatient(patient: PatientWithStats) {
    setSelectedPatient(patient);
    setView('record');
  }

  function handleBack() {
    setView('list');
    setSelectedPatient(null);
  }

  if (loading) {
    return (
      <div className="clinical-records">
        <TableSkeleton rows={9} chips={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="clinical-records">
        <div className="cr-empty-state">
          <i className="fas fa-exclamation-circle cr-empty-state__icon" aria-hidden="true"></i>
          <p className="cr-empty-state__body">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="clinical-records">
        {view === 'list' ? (
          <PatientListView
            patients={patients}
            onSelectPatient={handleSelectPatient}
            onSchedule={handleSchedulePatient}
            onDelete={handleDeleteRequest}
            onRegister={() => setShowRegistration(true)}
            adminMode={adminMode}
          />
        ) : selectedPatient ? (
          <ClinicalRecordView
            patient={selectedPatient}
            therapistId={therapist.id}
            callingTherapist={therapist}
            onBack={handleBack}
            onSchedule={handleSchedulePatient}
            onPatientUpdated={(id, patch) => {
              setPatients(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
              setSelectedPatient(prev => prev && prev.id === id ? { ...prev, ...patch } : prev);
            }}
            adminMode={adminMode}
          />
        ) : null}
      </div>

      {showScheduler && (
        <AppointmentScheduler
          therapistId={therapist.id}
          preSelectedPatient={schedulerPatient ? {
            id: schedulerPatient.id,
            rut: schedulerPatient.rut,
            nombre: schedulerPatient.nombre,
            apellido: schedulerPatient.apellido,
            email: schedulerPatient.email,
            telefono: schedulerPatient.telefono ?? '',
          } : undefined}
          onAppointmentCreated={() => { setShowScheduler(false); setSchedulerPatient(null); }}
          onCancel={() => { setShowScheduler(false); setSchedulerPatient(null); }}
        />
      )}

      {showRegistration && (
        <PatientRegistration
          compact={compactRegistration || adminMode}
          therapistId={therapist.id}
          onPatientRegistered={handlePatientRegistered}
          onCancel={() => setShowRegistration(false)}
        />
      )}

      {deleteConfirmPatient && (
        <div className="cr-modal-overlay" role="dialog" aria-modal="true" aria-label="Confirmar eliminación">
          <div className="cr-modal">
            <h3 className="cr-modal__title">
              <i className="fas fa-exclamation-triangle cr-modal__icon" aria-hidden="true"></i>
              Eliminar paciente
            </h3>
            <p className="cr-modal__body">
              ¿Eliminar a{' '}
              <strong>{deleteConfirmPatient.nombre} {deleteConfirmPatient.apellido}</strong>?
              Esta acción no se puede deshacer.
            </p>
            {deleteError && <p className="cr-modal__error" role="alert">{deleteError}</p>}
            <div className="cr-modal__actions">
              <button
                className="cr-btn cr-btn--danger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Eliminando…' : 'Confirmar eliminación'}
              </button>
              <button
                className="cr-btn cr-btn--ghost"
                onClick={() => { setDeleteConfirmPatient(null); setDeleteError(null); }}
                disabled={deleteLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
